'use client';

import { useTranslations } from "next-intl";
import { useState, useEffect, useRef, lazy, Suspense, useCallback } from "react";
import { List, ListItem } from "./components/menu-list";
import UserInfo from './components/user-info';
import useFn from "@/hooks/use-fn";
import { getLogout } from "../auth/services/logout";
import { authSignout } from "../auth/services/api-auth";
import toast from "react-hot-toast";
import { useSetAtom } from "jotai";
import { setFlagAtom, showUserCenterDrawerAtom } from "@/store";
import { useWalletDisconnect } from "@/hooks/use-wallet-disconnect";
import UserIcon from '@/features/user-center/components/user-icon/index';
import clsx from "clsx";
import Image from "next/image";
import { isInApp } from "@/libs/comeAppSDK";
import { showModalSeconsConfirm } from "./components/second-confirm";
import { commonPage, loginPage } from "@/const/page-url";
import { LANGUAGE_ICON_MAP } from "./constants";
import useSwitchLanguage from "./hooks/useSwitchLanguage";
import { thinkingLogout, track } from "@/utils/track";
import { showNotificationDrawer } from '@/features/notifications/index';
import { useNotificationUnreadNum } from '@/features/notifications/services';
import useIsLogin from '@/features/notifications/hooks/useIsLogin';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useContactCustomerService } from "@/components/contact-customer-service/use-contact-customer-service";
import { useGetAgentStatus } from "./services/agent";
import { showModalSetting } from './components/setting-modal';
import { useAtom } from 'jotai';
import { unreadCountInfoAtom } from '@/store/notifications';
import hideBgImg from '@/features/user-center/images/bg-hide.png';
import { getSafeInsets } from "@/utils/browser";
import SideDrawer from './components/side-drawer';
import { Spin } from '@/components/ui-feat/spin';
import { useRouter } from "@/i18n/navigation";
import { LANGUAGE_MAP } from "@/const/language";
import { useLoginResult } from "../auth/hooks/use-login-result";
import { findLocale } from "@/libs/find-locale";
import { Env } from "@/libs/env";
import IconFont from "@/components/ui/iconFont";
import { GUEST_AUTO_LOGIN_OUT } from "@/const/storage-key";
import { useGetVipUrl } from "@/services/chat-vip-url";
import { useShowAppBanner } from "@/components/layout/tab-menu/hooks/use-show-appbanner";
import UserLevel from '@/features/user-vip/components/user-level';

const AppInfo = lazy(() => import('@/features/user-center/components/app-info'));

interface UserCenterProps {
  onClose?: () => void;
}

const NoticeDot = ({ className, noticeNum }: { className?: string, noticeNum: number; }) => {
  if (noticeNum > 0) {
    return (
      <p className={clsx("bg-accent-red min-w-[.8125rem] h-3 rounded-full text-[.68755rem] text-white flex items-center justify-center ml-[2px] px-[3px] leading-none", className)}>
        {noticeNum > 99 ? '99+' : noticeNum}
      </p>
    );
  }
  return null;
};

const LanguageSelect = ({ language }: { language: string; }) => {
  return (
    <div className="flex items-center cursor-pointer">
      <Image className="w-4 h-4 mr-[.3125rem]" src={LANGUAGE_ICON_MAP[language]} alt={language} width={16} height={16} />
      <p className="font-medium text-ss text-secondary leading-[.5625rem]">{LANGUAGE_MAP[language]}</p>
      <IconFont name="arrow-down" size={10} color='#a9b5cb' className='ml-2.5' />
    </div>
  );
};

const PersonalCenter = NiceModal.create<UserCenterProps>(({ onClose }: UserCenterProps) => {
  const modal = useModal();
  const t = useTranslations('personal');
  const { isLogin } = useIsLogin();
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const isShowAppBanner = useShowAppBanner();
  useEffect(() => {
    setIsLoading(true);
  }, []);
  const [unreadCountInfo, setUnreadCountInfo] = useAtom(unreadCountInfoAtom);
  const { data: vipCustomerData } = useGetVipUrl();
  // 用于防止重复移除的标志
  const isRemovingRef = useRef(false);
  const prevVisibleRef = useRef<boolean | undefined>(undefined);
  // 解决快速点击问题：记录vip详情是否已经加载完成
  const [isVipDetailLoaded, setIsVipDetailLoaded] = useState(false);

  const handleClose = () => {
    onClose?.();
    modal.resolve(true);
    // 先隐藏，触发动画
    modal.hide();
    // 标记正在移除，防止 useEffect 重复处理
    isRemovingRef.current = true;
    // 等待动画完成后再移除组件
    setTimeout(() => {
      modal.remove();
      isRemovingRef.current = false;
    }, 300);
  };

  // 处理外部调用 closeUserCenter 的情况
  // 当 visible 从 true 变为 false 时（但不是通过 handleClose），也需要等待动画后移除
  useEffect(() => {
    // 初始化 ref
    if (prevVisibleRef.current === undefined) {
      prevVisibleRef.current = modal.visible;
      return;
    }
    // 如果从 true 变为 false，且还没有开始移除流程（说明是外部调用 closeUserCenter）
    if (prevVisibleRef.current === true && modal.visible === false && !isRemovingRef.current) {
      isRemovingRef.current = true;
      const timer = setTimeout(() => {
        modal.remove();
        isRemovingRef.current = false;
      }, 300);
      prevVisibleRef.current = false;
      return () => {
        clearTimeout(timer);
        isRemovingRef.current = false;
      };
    }
    // 更新 ref
    if (prevVisibleRef.current !== modal.visible) {
      prevVisibleRef.current = modal.visible;
    }
  }, [modal.visible, modal]);

  // 获取站内信未读数量，仅在用户登录时请求
  const { data: unreadData, refetch: refetchUnreadNum } = useNotificationUnreadNum({
    enabled: isLogin,
  });

  useEffect(() => {
    if (unreadData) {
      setUnreadCountInfo({
        allUnreadCount: unreadData.all_unread_count,
        platformUnreadCount: unreadData.platform_unread_count,
        personalUnreadCount: unreadData.personal_unread_count,
      });
    }
  }, [unreadData]);

  // 当抽屉可见时，刷新未读数量
  useEffect(() => {
    if (modal.visible) {
      refetchUnreadNum();
    }
  }, [modal.visible, refetchUnreadNum]);

  const setSessionFlag = useSetAtom(setFlagAtom);
  const { triggerDisconnect } = useWalletDisconnect();
  const { logoutClear } = useLoginResult();

  const handleLogout = useFn(async () => {
    closeUserCenter();
    const res = await showModalSeconsConfirm({ content: t('account.confirmLogout') });
    if (!res) {
      return;
    }
    try {
      // 同时调用后端登出接口和第三方登出接口
      await Promise.all([
        getLogout(),
        authSignout({ params: {} }).catch(err => {
          // 第三方登出失败不影响主流程
          console.warn('Third party logout failed:', err);
        }),
      ]);
    } catch (error) {
      console.error('Logout failed:', error);
      toast(t('errors.logoutFailed'));
    }
    logoutClear();
    setSessionFlag('logout');
    triggerDisconnect();
    // 数数埋点退出登录
    thinkingLogout();
    // 恢复动画
    sessionStorage.removeItem('preloader-video-played');

    // 获取locale并跳转
    const currentLocale = findLocale(window.location.pathname);
    // 将完整的 locale (如 'en-US') 转换为前缀 (如 'en')
    const localePrefix = currentLocale.split('-')[0];
    const getLogoutCallbackUrl = () => {
      // 确保在客户端环境
      if (typeof window === 'undefined') {
        return '/';
      }
      return `/${localePrefix}`;
    };

    // app环境
    if (isInApp()) {
      // 跳转页面
      router.push(`/login`);
      // 游客增加退出标记
      localStorage.setItem(GUEST_AUTO_LOGIN_OUT, '1');
      return;
    } else {
      // 非app环境，跳转到对应语言的首页
      window.location.href = getLogoutCallbackUrl();
    }
  });

  const { language } = useSwitchLanguage();

  const handleTrack = ({ href = '', trackName = '' }) => {
    const trackPageName = href;
    track('profile_page_button_click', {
      current_page: 'Personal Center',
      button_name: trackName || trackPageName,
    });
  };

  const { data: agentStatusData } = useGetAgentStatus.useQueryHook({
    params: {}
  });

  const { handleContact } = useContactCustomerService({ source: 'Personal Center' });
  const handleContactCustomer = useFn(() => {
    handleContact();
  });

  const handleShowNotificationDrawer = useFn(async () => {
    closeUserCenter();
    showNotificationDrawer();
  });

  const [safearea, setSafearea] = useState({ topSafe: 0, bottomSafe: 0 });

  useEffect(() => {
    getSafeInsets().then((res) => {
      setSafearea(res);
    });
  }, []);

  const setShowUserCenterDrawer = useSetAtom(showUserCenterDrawerAtom);

  useEffect(() => {
    if (modal.visible) {
      setShowUserCenterDrawer(true);
      setIsLoading(!isVipDetailLoaded);
    } else {
      setShowUserCenterDrawer(false);
    }
  }, [modal.visible, isVipDetailLoaded]);

  // 预加载router
  useEffect(() => {
    router.prefetch(loginPage.login);
  }, [router]);

  const onVipDataLoad = useCallback(() => {
    setIsLoading(false);
    setIsVipDetailLoaded(true);
  }, []);

  return (
    <SideDrawer onModalClick={handleClose} visible={modal.visible} onClose={handleClose}>
      <div onClick={handleClose} className="pl-6 h-full min-h-screen">
        <div onClick={handleClose} className="absolute w-6 h-[5.375rem] rounded-l-[8px] left-0.5 top-[50%] translate-y-[-50%] flex items-center justify-center"
          style={{
            backgroundImage: `url(${hideBgImg.src})`,
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <IconFont name="arrow-back" size={15} className="rotate-180" />
        </div>
        {isLoading && <Spin className="w-full h-full" />}
        <div
          onClick={(e) => e.stopPropagation()}
          className={clsx(
            "h-full py-[3.8125rem] px-5 bg-[#1B202A] overflow-y-auto no-scrollbar",
            isInApp() ? 'pb-[70px]' : 'pb-[90px]'
          )} style={{
            paddingTop: isInApp() ? `calc(3.8125rem + ${safearea.topSafe}px)` : '3.8125rem',
            height: `${window.innerHeight}px`
          }}
        >
          {isShowAppBanner ? <div className="w-full h-[65px]" /> : null}
          <UserInfo className="mb-[1.5625rem]" />
          <UserLevel onVipDataLoad={onVipDataLoad} onBeforeLinkJump={handleClose} />
          <div className="line w-full h-[1px] bg-[#D6EAFF] opacity-10 mt-[18px] mb-2.5"></div>
          <List
            className="bg-transparent"
            itemLeftIconClassName="w-[1.4375rem] h-[1.4375rem] mr-3"
            itemTitleClassName="font-medium text-xs !text-secondary leading-6"
          >
            <ListItem
              title={t('notification.notifications')}
              onClick={handleShowNotificationDrawer}
              onClickTrack={handleTrack}
              trackName="Notifications"
              type="short"
              leftIcon={<UserIcon name="notify" status="colorfull" width={23} height={23} />}
              rightContent={
                <NoticeDot noticeNum={unreadCountInfo?.allUnreadCount || 0} className="mr-2.5" />
              }
            />
            <ListItem
              title={t('navigation.account')}
              type="short"
              onBeforeLinkJump={handleClose}
              onClickTrack={handleTrack}
              trackName="Account Settings"
              href="/account"
              leftIcon={<UserIcon name="account-card" status="colorfull" />}
            />
            <ListItem
              title={t('history.transactionHistory')}
              href="/transaction-history"
              onBeforeLinkJump={handleClose}
              onClickTrack={handleTrack}
              trackName="Transactions"
              type="short"
              leftIcon={<UserIcon name="transaction-history" status="colorfull" />}
            />
            <ListItem
              title={t('history.gameHistory')} type="short"
              href="/game-history"
              onBeforeLinkJump={handleClose}
              onClickTrack={handleTrack}
              trackName="Game History"
              leftIcon={<UserIcon name="game-history" status="colorfull" />}
            />
            {
              agentStatusData?.status === 1 && (
                <ListItem
                  title={t('notification.referralProgram')}
                  type="short"
                  href="/affiliate"
                  onBeforeLinkJump={handleClose}
                  onClickTrack={handleTrack}
                  trackName="Affiliate"
                  leftIcon={<UserIcon name="agent-fission" status="colorfull" />}
                />
              )
            }
            <ListItem
              title={t('vip.vipBenefits')}
              type="short"
              onBeforeLinkJump={handleClose}
              href={commonPage.vip}
              onClickTrack={handleTrack}
              trackName="VIP Club"
              leftIcon={<UserIcon name="vip-benefits" status="colorfull" />} />
            <ListItem
              title={t('contactCustomer')}
              type="short"
              onBeforeLinkJump={handleClose}
              onClick={handleContactCustomer}
              onClickTrack={handleTrack}
              trackName="Contact CS"
              leftIcon={<UserIcon name={vipCustomerData?.enable ? "customer-vip" : "customer"} status="colorfull" />} />
            <ListItem
              title={t('navigation.settings')}
              type="short"
              onClick={() => {
                showModalSetting();
                handleClose();
              }}
              onClickTrack={handleTrack}
              trackName="Settings"
              leftIcon={<UserIcon name="setting" status="colorfull" />} arrow={false}
              extra={
                <LanguageSelect language={language || 'en-US'} />
              }
            />
            <ListItem
              onClick={handleLogout}
              title={t('account.logout')}
              type="short"
              leftIcon={<UserIcon name="logout" status="colorfull" />} />
          </List>
          {(Env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === 'test' || Env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === 'pre') && (
            <p className="mt-[1.875rem] text-center text-tertiary text-ss leading-3 font-medium pb-[20px]">
              <span>(debug version: {Env.NEXT_PUBLIC_SENTRY_ENVIRONMENT}-{Env.RELEASE})</span>
            </p>
          )}
        </div>
        {!isLoading && !isInApp() && <Suspense fallback={null}>
          <AppInfo handleClose={handleClose} />
        </Suspense>}
      </div>
    </SideDrawer >
  );
});

PersonalCenter.displayName = 'PersonalCenter';
// eslint-disable-next-line react-refresh/only-export-components
export const showUserCenter: (props?: UserCenterProps) => Promise<boolean> = (props) => {
  return NiceModal.show(PersonalCenter, props);
};

export const closeUserCenter: () => void = () => {
  // 直接调用 hide，这会改变 modal.visible 状态
  // SideDrawer 组件会响应 visible 变化并播放关闭动画
  // 组件内部的 handleClose 会在适当的时机被调用（比如点击外部时）
  // 但这里我们需要手动触发关闭流程
  NiceModal.hide(PersonalCenter);
};

export default PersonalCenter;
